import { NetworkStatus, QueryHookOptions, useQuery } from '@apollo/client';
import { pathEq } from 'ramda';

import { PaymentCardProductType, PaymentCardStatus } from '__generated__/GQL';
import mergeConnectionResultsBy from 'common/graphql/mergeConnectionResultsBy';
import useCompanyContext from 'common/hooks/useCompanyContext';
import {
  PaymentCardListTable_Item_CreditCard_Fragment,
  PaymentCardListTable_ItemFragment,
  PaymentCardsListDocument,
  PaymentCardsListQuery,
  PaymentCardsListQueryVariables,
} from 'features/PaymentCards/pages/NewPaymentCardList/hooks/usePaymentCardsList/paymentCardsList.gql';
import { CardsView } from 'features/PaymentCards/pages/NewPaymentCardList/types';

import { usePaymentCardsListState } from '../../components/PaymentCardsListFiltersProvider';

export type PaymentCardsListListPageInfo =
  PaymentCardsListQuery['viewer']['company']['paymentCards']['pageInfo'];

export const usePaymentCardsList = (
  options?: QueryHookOptions<
    PaymentCardsListQuery,
    PaymentCardsListQueryVariables
  >,
) => {
  const { companyProfileId, companyUserId } = useCompanyContext();

  const {
    filters: { cardProductType, status },
    ordering: {
      currentOrdering: { orderBy, sortBy },
    },
    view,
  } = usePaymentCardsListState();

  const { data, fetchMore, previousData, ...hooks } = useQuery(
    PaymentCardsListDocument,
    {
      ...options,
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      variables: {
        companyProfileId: companyProfileId ?? '',
        companyUserId: view === CardsView.OWN ? companyUserId ?? '' : null,
        filters: {
          cardProductType: cardProductType as PaymentCardProductType[],
          status: status as PaymentCardStatus[],
        },
        limit: 20,
        offset: null,
        orderBy,
        sortBy,
      },
    },
  );

  const loadMore = () => {
    return fetchMore({
      updateQuery: (previousResult, { fetchMoreResult }) => {
        const isEqual = pathEq(
          previousResult,
          ['viewer', 'company', 'paymentCards', 'pageInfo'],
          fetchMoreResult,
        );
        if (isEqual) {
          return previousResult;
        }
        return mergeConnectionResultsBy('creditCardId')(
          previousResult,
          fetchMoreResult,
        );
      },
      variables: {
        offset: data?.viewer.company.paymentCards.pageInfo.nextOffset,
      },
    });
  };

  const paymentCards =
    data?.viewer.company.paymentCards.edges?.map((edge) => edge.node) ??
    undefined;

  const previousDataPaymentCards =
    previousData?.viewer.company.paymentCards.edges?.map((edge) => edge.node) ??
    undefined;

  const isFetchingMore = hooks.networkStatus === NetworkStatus.fetchMore;

  const pageInfo =
    data?.viewer.company.paymentCards.pageInfo ??
    ({
      __typename: 'PaymentCardsPageInfo',
      hasNextPage:
        previousData?.viewer.company.paymentCards.pageInfo.hasNextPage ?? false,
      nextOffset:
        previousData?.viewer.company.paymentCards.pageInfo.nextOffset ?? null,
      totalCount:
        previousData?.viewer.company.paymentCards.pageInfo.totalCount ?? 0,
    } satisfies PaymentCardsListListPageInfo);

  return {
    ...hooks,
    isFetchingMore,
    loadMore,
    pageInfo,
    paymentCards,
    previousDataPaymentCards,
  } as const;
};

export type PaymentCardListObservableQuery = ReturnType<
  typeof usePaymentCardsList
>['observable'];

export const isCreditCardFragment = (
  card: PaymentCardListTable_ItemFragment,
): card is PaymentCardListTable_Item_CreditCard_Fragment => {
  return card.__typename === 'CreditCard';
};
